import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { ClipboardCheck, Plus, CheckCircle, XCircle, Clock, Search, Filter, Eye, Edit, Trash2, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const QualityControl = () => {
  const [activeTab, setActiveTab] = useState("tests");
  const [materialTests, setMaterialTests] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [stats, setStats] = useState(null);

  // Test Dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testForm, setTestForm] = useState({
    project_id: "",
    material_name: "",
    test_type: "Compressive Strength",
    sample_id: "",
    test_date: new Date().toISOString().split('T')[0],
    lab_name: "",
    specified_value: "",
    actual_value: "",
    unit: "",
    result: "Pending",
    remarks: ""
  });

  // Inspection Dialog
  const [inspectionDialogOpen, setInspectionDialogOpen] = useState(false);
  const [inspectionForm, setInspectionForm] = useState({
    project_id: "",
    date: new Date().toISOString().split('T')[0],
    inspection_type: "Concrete Placement",
    location: "",
    inspector_name: "",
    items: [
      { item: "Formwork alignment checked", status: "Pass", remarks: "" },
      { item: "Reinforcement as per drawing", status: "Pass", remarks: "" },
      { item: "Cover blocks in place", status: "Pass", remarks: "" },
      { item: "Concrete grade verified", status: "Pass", remarks: "" },
      { item: "Slump test conducted", status: "Pass", remarks: "" },
      { item: "Vibration adequate", status: "Pass", remarks: "" },
      { item: "Curing arrangements made", status: "Pass", remarks: "" }
    ],
    overall_result: "Approved",
    corrective_actions: "",
    remarks: ""
  });

  const TEST_TYPES = [
    "Compressive Strength", "Tensile Strength", "Slump Test", "Gradation",
    "Moisture Content", "CBR Test", "Atterberg Limits", "Proctor Test",
    "Bitumen Content", "Marshall Stability", "Aggregate Impact", "Los Angeles Abrasion",
    "Specific Gravity", "Water Absorption", "Fineness Modulus", "Other"
  ];

  const INSPECTION_TYPES = [
    "Concrete Placement", "Reinforcement", "Formwork", "Earthwork",
    "Sub-grade", "Sub-base", "Base Course", "Bituminous Work",
    "Drainage", "Structures", "Final Inspection", "Pre-pour", "Other"
  ];

  const currentUser = getUser() || {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProjects();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedProject) {
      fetchData();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [testRes, inspRes, statsRes, matRes] = await Promise.all([
        apiClient.get(`/quality/material-tests?project_id=${selectedProject}`),
        apiClient.get(`/quality/inspections?project_id=${selectedProject}`),
        apiClient.get(`/quality/stats?project_id=${selectedProject}`),
        apiClient.get(`/materials/${selectedProject}`)
      ]);
      setMaterialTests(testRes.data);
      setInspections(inspRes.data);
      setStats(statsRes.data);
      setMaterials(matRes.data);
    } catch (error) {
      toast.error("Failed to fetch quality data");
    } finally {
      setLoading(false);
    }
  };

  const handleTestSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...testForm,
        project_id: selectedProject,
        specified_value: parseFloat(testForm.specified_value) || 0,
        actual_value: parseFloat(testForm.actual_value) || 0
      };
      await apiClient.post('/quality/material-tests', data);
      toast.success("Test record added");
      setTestDialogOpen(false);
      resetTestForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to add test record");
    }
  };

  const handleInspectionSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/quality/inspections', { ...inspectionForm, project_id: selectedProject });
      toast.success("Inspection recorded");
      setInspectionDialogOpen(false);
      resetInspectionForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to record inspection");
    }
  };

  const resetTestForm = () => {
    setTestForm({
      project_id: selectedProject,
      material_name: "",
      test_type: "Compressive Strength",
      sample_id: "",
      test_date: new Date().toISOString().split('T')[0],
      lab_name: "",
      specified_value: "",
      actual_value: "",
      unit: "",
      result: "Pending",
      remarks: ""
    });
  };

  const resetInspectionForm = () => {
    setInspectionForm({
      project_id: selectedProject,
      date: new Date().toISOString().split('T')[0],
      inspection_type: "Concrete Placement",
      location: "",
      inspector_name: "",
      items: [
        { item: "Formwork alignment checked", status: "Pass", remarks: "" },
        { item: "Reinforcement as per drawing", status: "Pass", remarks: "" },
        { item: "Cover blocks in place", status: "Pass", remarks: "" },
        { item: "Concrete grade verified", status: "Pass", remarks: "" },
        { item: "Slump test conducted", status: "Pass", remarks: "" },
        { item: "Vibration adequate", status: "Pass", remarks: "" },
        { item: "Curing arrangements made", status: "Pass", remarks: "" }
      ],
      overall_result: "Approved",
      corrective_actions: "",
      remarks: ""
    });
  };

  const updateInspectionItem = (index, field, value) => {
    setInspectionForm(prev => {
      const items = [...prev.items];
      items[index][field] = value;
      return { ...prev, items };
    });
  };

  const getResultBadge = (result) => {
    const config = {
      "Pass": { color: "bg-success/20 text-success", icon: CheckCircle },
      "Fail": { color: "bg-error/20 text-error", icon: XCircle },
      "Pending": { color: "bg-warning/20 text-warning", icon: Clock },
      "Approved": { color: "bg-success/20 text-success", icon: CheckCircle },
      "Rejected": { color: "bg-error/20 text-error", icon: XCircle },
      "Conditional": { color: "bg-warning/20 text-warning", icon: AlertTriangle }
    };
    const { color, icon: Icon } = config[result] || config["Pending"];
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        <span>{result}</span>
      </span>
    );
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">QUALITY CONTROL</h1>
          <p className="text-slate-600 mt-2 font-body">Material testing and inspection records</p>
        </div>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-sm"
        >
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Total Tests</p>
          <p className="font-heading font-black text-3xl text-primary">{stats?.total_tests || 0}</p>
        </div>
        <div className="stat-card border-l-4 border-l-success">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Pass Rate</p>
          <p className="font-heading font-black text-3xl text-success">{stats?.pass_rate || 0}%</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Inspections</p>
          <p className="font-heading font-black text-3xl text-accent">{stats?.total_inspections || 0}</p>
        </div>
        <div className="stat-card border-l-4 border-l-warning">
          <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Pending Tests</p>
          <p className="font-heading font-black text-3xl text-warning">{stats?.pending_tests || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        {[
          { id: "tests", label: "Material Tests", icon: FileText },
          { id: "inspections", label: "Inspections", icon: ClipboardCheck }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 px-4 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id ? 'border-b-2 border-primary text-primary' : 'text-slate-600'}`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Material Tests Tab */}
      {activeTab === "tests" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { resetTestForm(); setTestDialogOpen(true); }} className="btn-primary flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Add Test Record</span>
            </button>
          </div>
          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Material</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Test Type</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Sample ID</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Specified</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Actual</th>
                  <th className="text-center py-3 px-4 text-xs uppercase">Result</th>
                  <th className="text-left py-3 px-4 text-xs uppercase">Lab</th>
                </tr>
              </thead>
              <tbody>
                {materialTests.map(test => (
                  <tr key={test.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono text-sm">{test.test_date}</td>
                    <td className="py-3 px-4 text-sm font-medium">{test.material_name}</td>
                    <td className="py-3 px-4 text-sm">{test.test_type}</td>
                    <td className="py-3 px-4 font-mono text-sm">{test.sample_id}</td>
                    <td className="py-3 px-4 text-center font-mono text-sm">{test.specified_value} {test.unit}</td>
                    <td className="py-3 px-4 text-center font-mono text-sm font-bold">{test.actual_value} {test.unit}</td>
                    <td className="py-3 px-4 text-center">{getResultBadge(test.result)}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{test.lab_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {materialTests.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-xl mb-2">No Test Records</h3>
                <p>Add material test results to track quality.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Inspections Tab */}
      {activeTab === "inspections" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { resetInspectionForm(); setInspectionDialogOpen(true); }} className="btn-primary flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>New Inspection</span>
            </button>
          </div>
          <div className="space-y-3">
            {inspections.length > 0 ? inspections.map(insp => {
              const passCount = insp.items?.filter(i => i.status === 'Pass').length || 0;
              const totalCount = insp.items?.length || 1;
              return (
                <div key={insp.id} className={`stat-card border-l-4 ${insp.overall_result === 'Approved' ? 'border-l-success' : insp.overall_result === 'Rejected' ? 'border-l-error' : 'border-l-warning'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-medium">{insp.inspection_type}</span>
                        {getResultBadge(insp.overall_result)}
                      </div>
                      <p className="text-sm text-slate-600">{insp.location}</p>
                      <p className="text-xs text-slate-500 mt-1">{insp.date} | Inspector: {insp.inspector_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{passCount}/{totalCount}</p>
                      <p className="text-xs text-slate-500">Items Passed</p>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="text-center py-12 stat-card">
                <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-xl mb-2">No Inspections</h3>
                <p className="text-slate-600">Record quality inspections for work activities.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">ADD MATERIAL TEST RECORD</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTestSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Material Name *</label>
                <input type="text" required value={testForm.material_name} onChange={(e) => setTestForm({...testForm, material_name: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" placeholder="e.g., Cement, Aggregate" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Test Type *</label>
                <select required value={testForm.test_type} onChange={(e) => setTestForm({...testForm, test_type: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  {TEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Sample ID *</label>
                <input type="text" required value={testForm.sample_id} onChange={(e) => setTestForm({...testForm, sample_id: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" placeholder="e.g., CUB-001" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Test Date *</label>
                <input type="date" required value={testForm.test_date} onChange={(e) => setTestForm({...testForm, test_date: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Lab Name</label>
                <input type="text" value={testForm.lab_name} onChange={(e) => setTestForm({...testForm, lab_name: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Specified Value *</label>
                <input type="number" step="0.01" required value={testForm.specified_value} onChange={(e) => setTestForm({...testForm, specified_value: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Actual Value *</label>
                <input type="number" step="0.01" required value={testForm.actual_value} onChange={(e) => setTestForm({...testForm, actual_value: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Unit</label>
                <input type="text" value={testForm.unit} onChange={(e) => setTestForm({...testForm, unit: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" placeholder="MPa, %, mm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Result *</label>
                <select required value={testForm.result} onChange={(e) => setTestForm({...testForm, result: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  <option value="Pending">Pending</option>
                  <option value="Pass">Pass</option>
                  <option value="Fail">Fail</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Remarks</label>
              <textarea value={testForm.remarks} onChange={(e) => setTestForm({...testForm, remarks: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setTestDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">Save Test Record</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inspection Dialog */}
      <Dialog open={inspectionDialogOpen} onOpenChange={setInspectionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">QUALITY INSPECTION</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInspectionSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Inspection Type *</label>
                <select required value={inspectionForm.inspection_type} onChange={(e) => setInspectionForm({...inspectionForm, inspection_type: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  {INSPECTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Date *</label>
                <input type="date" required value={inspectionForm.date} onChange={(e) => setInspectionForm({...inspectionForm, date: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Location *</label>
                <input type="text" required value={inspectionForm.location} onChange={(e) => setInspectionForm({...inspectionForm, location: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" placeholder="e.g., Foundation F1, KM 5+200" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Inspector Name *</label>
                <input type="text" required value={inspectionForm.inspector_name} onChange={(e) => setInspectionForm({...inspectionForm, inspector_name: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" />
              </div>
            </div>
            <div className="border border-slate-200 rounded-sm">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs uppercase">Check Item</th>
                    <th className="text-center py-2 px-3 text-xs uppercase w-24">Status</th>
                    <th className="text-left py-2 px-3 text-xs uppercase w-40">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {inspectionForm.items.map((item, idx) => (
                    <tr key={idx} className="border-t border-slate-200">
                      <td className="py-2 px-3 text-sm">{item.item}</td>
                      <td className="py-2 px-3">
                        <select value={item.status} onChange={(e) => updateInspectionItem(idx, 'status', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm">
                          <option value="Pass">Pass</option>
                          <option value="Fail">Fail</option>
                          <option value="NA">N/A</option>
                        </select>
                      </td>
                      <td className="py-2 px-3">
                        <input type="text" value={item.remarks} onChange={(e) => updateInspectionItem(idx, 'remarks', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-sm" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Overall Result *</label>
                <select required value={inspectionForm.overall_result} onChange={(e) => setInspectionForm({...inspectionForm, overall_result: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm">
                  <option value="Approved">Approved</option>
                  <option value="Conditional">Conditional Approval</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Corrective Actions</label>
                <input type="text" value={inspectionForm.corrective_actions} onChange={(e) => setInspectionForm({...inspectionForm, corrective_actions: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase">Remarks</label>
              <textarea value={inspectionForm.remarks} onChange={(e) => setInspectionForm({...inspectionForm, remarks: e.target.value})} className="w-full px-4 py-2 border border-slate-300 rounded-sm" rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setInspectionDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary">Submit Inspection</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QualityControl;
